/**
 * Pure helpers for grouping/family structure edits (moved from WSRADM-0026 DimensionStructureEditor).
 */
import { FAMILY_PALETTE } from '@/components/familyPalette';
import type {
  DimensionStructure,
  StructureDimension,
  StructureFamily,
  StructurePutBody,
} from '@/api/dimensions/types';
import { detailsCode } from './detailsCode';

export type StructureSaveErrorKey =
  | 'duplicateSlug'
  | 'invalidName'
  | 'invalidColor'
  | 'readOnly'
  | 'saveFailed';

export const STRUCTURE_SAVE_ERROR_FALLBACK: Record<StructureSaveErrorKey, string> = {
  duplicateSlug: 'Two dimensions or families would share the same slug.',
  invalidName: 'A name is blank or invalid.',
  invalidColor: 'A colour is not a valid #RRGGBB value.',
  readOnly: 'A read-only dimension cannot be saved.',
  saveFailed: 'Could not save the classification structure.',
};

export function cloneStructure(structure: DimensionStructure): DimensionStructure {
  return {
    dimensions: structure.dimensions.map((dimension) => ({
      ...dimension,
      families: dimension.families.map((family) => ({ ...family })),
    })),
  };
}

export function maxSortOrder(items: Array<{ sortOrder: number }>): number {
  if (items.length === 0) return -1;
  return Math.max(...items.map((item) => item.sortOrder));
}

export function nextPaletteColor(families: StructureFamily[]): string | null {
  const paletteUpper = FAMILY_PALETTE.map((entry) => entry.toUpperCase());
  const used = new Set(
    families
      .map((family) => family.color?.toUpperCase())
      .filter((color): color is string => color !== undefined)
      .filter((color) => paletteUpper.includes(color)),
  );
  for (const entry of FAMILY_PALETTE) {
    if (!used.has(entry.toUpperCase())) return entry;
  }
  return null;
}

export function toPutBody(draft: DimensionStructure): StructurePutBody {
  return {
    dimensions: draft.dimensions
      .filter((dimension) => dimension.readOnly !== true)
      .map((dimension) => ({
        slug: dimension.slug,
        displayName: dimension.displayName,
        sortOrder: dimension.sortOrder,
        families: dimension.families.map((family) => ({
          slug: family.slug,
          displayName: family.displayName,
          sortOrder: family.sortOrder,
          color: family.color,
          role: family.role,
        })),
      })),
  };
}

export function mapSaveError(err: unknown): StructureSaveErrorKey {
  const code = detailsCode(err);
  if (code === 'DUPLICATE_SLUG') return 'duplicateSlug';
  if (code === 'INVALID_SLUG') return 'invalidName';
  if (code === 'INVALID_COLOR') return 'invalidColor';
  if (code === 'DIMENSION_READ_ONLY') return 'readOnly';
  return 'saveFailed';
}

export function editableDimensionIndices(dimensions: StructureDimension[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < dimensions.length; i += 1) {
    if (dimensions[i]?.readOnly !== true) out.push(i);
  }
  return out;
}

/** Every family slug across all dimensions — the collision set for slugFromDisplayName. */
export function familySlugsOf(structure: DimensionStructure): string[] {
  const slugs: string[] = [];
  for (const dimension of structure.dimensions) {
    for (const family of dimension.families) {
      slugs.push(family.slug);
    }
  }
  return slugs;
}

/** True when two structures produce the same PUT body (the tenant dimensions, in order). */
export function sameStructureBody(a: DimensionStructure, b: DimensionStructure): boolean {
  return JSON.stringify(toPutBody(a)) === JSON.stringify(toPutBody(b));
}
